export type Point = {
  type: 'main' | 'sub';
  index: number;
  hide: boolean;
  position: number;
};

const DEFAULT_MIN_SUB_SPACE = 4;
const DEFAULT_MIN_MAIN_SPACE = 0;

/**
 * 点比例尺
 */
export default class PointScale {
  /** 值域 */
  private __range: [number, number] = [0, 0];

  /** 定义域 */
  private __domain: Point[] = [];

  /** 两侧外边距 */
  private __padding: [number, number] = [0, 0];

  /** 次要刻度最小间距（像素宽度），小于时会隐藏 */
  private __minSubSpace = DEFAULT_MIN_SUB_SPACE;

  private __minMainSpace = DEFAULT_MIN_MAIN_SPACE;

  private __followMainHide = false;

  constructor(range?: [number, number], domain?: Pick<Point, 'type' | 'index'>[]) {
    if (typeof range !== 'undefined') {
      this.range(range);
    }
    if (typeof domain !== 'undefined') {
      // deepClone
      this.domain(domain);
    }
  }

  /**
   * 计算解析
   */
  private __parse() {
    if (this.__range[0] === this.__range[1] || this.__domain.length === 0) {
      return;
    }

    const mainIndexes = this.__domain.reduce((indexes: number[], point, index) => {
      if (point.type === 'main') {
        return [...indexes, index];
      }
      return indexes;
    }, []);
    const mainNum = mainIndexes.length;
    // 按主刻度均分确定两个主刻度区间宽度
    const subRange = [
      0,
      (this.__range[1] - this.__padding[1] - this.__range[0] - this.__padding[0]) / (mainNum - 1)
    ];

    let currentMainNum = -1;
    let currentSubNum = 0;
    let skipSubNum = 0;
    let skipMainNum = 0;

    const domain: Point[] = [];
    let isHide = false;

    this.__domain.slice().forEach(point_ => {
      const point = { ...point_ };

      if (point.type === 'main') {
        currentMainNum += 1;
        currentSubNum = 0;
        skipSubNum = 0;

        const space = subRange[1] - subRange[0];
        while (space * skipMainNum < this.__minMainSpace) {
          skipMainNum += 1;
        }
        // ! 不满足最小间距的隐藏
        if (currentMainNum % skipMainNum !== 0 && this.__minMainSpace > 0) {
          point.hide = true;
          isHide = true;
        } else {
          point.hide = false;
        }
        point.position = this.__padding[0] + this.__range[0] + space * currentMainNum;
      } else {
        currentSubNum += 1;

        const currentMainIndex = mainIndexes[currentMainNum];
        const nextMainIndex = mainIndexes[currentMainNum + 1];
        const space = (subRange[1] - subRange[0]) / (nextMainIndex - currentMainIndex);

        while (space * skipSubNum < this.__minSubSpace) {
          skipSubNum += 1;
        }

        // ! 不满足最小间距的隐藏
        if (currentSubNum % skipSubNum !== 0) {
          point.hide = true;
        } else {
          point.hide = false;
        }

        point.position = domain[currentMainIndex].position + space * currentSubNum;
      }

      domain.push(point);
    });

    if (this.__followMainHide) {
      domain.forEach((point, index) => {
        if (point.type === 'sub' && isHide) {
          point.hide = true;
        }
      });
    }

    this.__domain = domain;
  }

  /**
   * 值域
   */
  range(range_?: [number, number]): [number, number] {
    if (typeof range_ !== 'undefined' && Array.isArray(range_) && range_.length > 0) {
      if (
        Number.isNaN(Number(range_[0])) ||
        Number.isNaN(Number(range_[1])) ||
        range_[0] >= range_[1]
      ) {
        throw new Error(`"range" should be: range[0] < range[1], ${JSON.stringify(range_)}`);
      }

      this.__range = [...range_];
      this.__parse();
    }

    return [...this.__range];
  }

  /**
   * 定义域
   */
  domain(domain_?: Pick<Point, 'type' | 'index'>[]) {
    if (typeof domain_ !== 'undefined' && Array.isArray(domain_) && domain_.length > 0) {
      // deepClone
      this.__domain = domain_.map(item => ({
        ...item,
        hide: false,
        position: 0
      }));
      this.__parse();
    }

    // deepClone
    return [...this.__domain];
  }

  /**
   * 两侧外边距
   */
  padding(padding_?: [number, number]): [number, number] {
    if (typeof padding_ !== 'undefined' && Array.isArray(padding_) && padding_.length > 0) {
      if (
        Number.isNaN(Number(padding_[0])) ||
        Number.isNaN(Number(padding_[1])) ||
        padding_[0] < 0 ||
        padding_[1] < 0
      ) {
        throw new Error(
          `"padding" should be: range[0] >= 0 and range[1] >= 0, ${JSON.stringify(padding_)}`
        );
      }

      this.__padding = [...padding_];
      this.__parse();
    }

    return [...this.__padding];
  }

  minSubSpace(pixelSpaceWidth?: number) {
    if (typeof pixelSpaceWidth !== 'undefined') {
      if (Number.isNaN(Number(pixelSpaceWidth)) || pixelSpaceWidth < 0) {
        throw new Error(`"minSubSpace" should be a positive number, ${pixelSpaceWidth}`);
      }

      this.__minSubSpace = pixelSpaceWidth;
      this.__parse();
    }

    return this.__minSubSpace;
  }

  minMainSpace(pixelSpaceWidth?: number) {
    if (typeof pixelSpaceWidth !== 'undefined') {
      if (Number.isNaN(Number(pixelSpaceWidth)) || pixelSpaceWidth < 0) {
        throw new Error(`"minMainSpace" should be a positive number, ${pixelSpaceWidth}`);
      }

      this.__minMainSpace = pixelSpaceWidth;
      this.__parse();
    }

    return this.__minMainSpace;
  }

  followMainHide(followMainHide: boolean) {
    this.__followMainHide = followMainHide;
  }

  /**
   * 获取点所在的坐标位置
   */
  at(index: Point['index']) {
    if (!this.__domain[index]) {
      throw new Error(
        `"index" ${index} should be one of: ${this.__domain.map(p => p.index).join(' | ')}`
      );
    }

    return this.__domain[index].position;
  }

  /**
   * 根据坐标位置获取当前点
   */
  findPointAt(position: Point['position']) {
    const range = this.__range.slice();

    if (!range.length || position < range[0] || position > range[1]) {
      throw new Error(`"position" ${position} should be within "range" ${JSON.stringify(range)}`);
    }

    const domain = this.__domain.slice();
    let index = 0;
    const MIDDLE = 0.5;

    for (let i = 0; i < domain.length; i += 1) {
      const currentPosition = domain[i].position;
      const nextPosition = domain[i + 1]?.position;

      if (currentPosition <= position && (!nextPosition || nextPosition > position)) {
        if (position <= currentPosition * (1 - MIDDLE) + nextPosition * MIDDLE) {
          index = i;
        } else {
          index = i + 1;
        }

        break;
      }
    }

    index = Math.max(0, index);
    index = Math.min(index, domain.length - 1);

    // deepClone
    return { ...this.__domain[index] };
  }
}
